import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Stack, Typography } from '@mui/material';

import {
  VOUCHER_AGENCY_TEXT_KEYS,
  VoucherTextIssue,
  VoucherTextLanguage,
  VoucherTextScope,
} from '@bandido/shared/types';

import { VoucherTextsDraft, voucherTextLimit } from '../voucherTexts';
import { TextBlockBox } from './TextBlockBox';

const KEY = 'vouchers.config';

interface TextsSectionProps {
  draft: VoucherTextsDraft;
  /** Los topes que publica el `GET`: el mismo número que rechaza el backend. */
  limits?: Record<string, number>;
  /** Lo que esta pantalla detecta, y lo que devolvió el `PUT`, ya unidos. */
  issues: Record<string, VoucherTextIssue[]>;
  /** Un rechazo que no cayó en ninguna caja: se dice, no se traga. */
  otrosRechazados?: boolean;
  canEdit: boolean;
  /** Por qué no se puede editar, cuando no se puede. */
  disabledReason?: string;
  onChange: (scope: VoucherTextScope, key: string, lang: VoucherTextLanguage, body: string) => void;
}

/**
 * TEXTOS de la agencia: lo que abre y lo que cierra TODOS sus documentos. La
 * pantalla no valida por su cuenta —el formato y los topes salen de shared, vía
 * `voucherTexts.ts`—, solo muestra lo que ese módulo decide.
 */
export const TextsSection: FC<TextsSectionProps> = ({
  draft,
  limits,
  issues,
  otrosRechazados,
  canEdit,
  disabledReason,
  onChange,
}) => {
  const { t } = useTranslation();
  return (
    <Stack gap={3}>
      <Alert severity="info">{t(`${KEY}.texts.appliesToAll`)}</Alert>
      <Typography variant="body2" color="text.secondary">
        {t(`${KEY}.texts.format`)}
      </Typography>

      {!canEdit && disabledReason && <Alert severity="warning">{t(disabledReason)}</Alert>}
      {otrosRechazados && <Alert severity="error">{t(`${KEY}.texts.otherRejected`)}</Alert>}

      {VOUCHER_AGENCY_TEXT_KEYS.map((blockKey) => (
        <TextBlockBox
          key={blockKey}
          scope="agency"
          blockKey={blockKey}
          draft={draft}
          limit={voucherTextLimit(limits, 'agency', blockKey)}
          issues={issues}
          canEdit={canEdit}
          title={t(`${KEY}.texts.${blockKey}`)}
          hint={t(`${KEY}.texts.${blockKey}Hint`)}
          onChange={onChange}
        />
      ))}
    </Stack>
  );
};
